import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';

const POLL_INTERVAL_MS = 10_000;

// IMP-008 / NL-029 — keep in lockstep with MESSAGE_BODY_MAX in
// apps/api/src/routes/v1/messages.ts. The counter fades in at 3500 per
// the NL-029 acceptance criterion.
const MESSAGE_MAX = 4_000;
const MESSAGE_COUNTER_FROM = 3_500;

/** ISO → "HH:mm" for the in-bubble timestamp (matches the macOS chat). */
function shortTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(11, 16);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Resolve which client thread we're viewing.
 *
 *  - Coach / head_coach: the `:clientId` URL param (set on /coach/clients/:clientId/messages).
 *  - Client: their own `user.clientId` (page wired at /messages with no param). */
function useThreadClientId(): { clientId: string | null; viewerRole: 'coach' | 'client' | null } {
  const { user } = useAuth();
  const params = useParams<{ clientId?: string }>();
  if (!user) return { clientId: null, viewerRole: null };
  if (user.role === 'client') {
    return { clientId: user.clientId ?? null, viewerRole: 'client' };
  }
  if (user.role === 'coach' || user.role === 'head_coach') {
    return { clientId: params.clientId ?? null, viewerRole: 'coach' };
  }
  return { clientId: null, viewerRole: null };
}

export function MessagesPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const { clientId, viewerRole } = useThreadClientId();

  const [messages, setMessages] = useState<api.ThreadMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const pageVisibleRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!clientId) return;
    try {
      const result = await api.listMessages(accessToken, clientId, { limit: 50 });
      setMessages(result.messages);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.errors.network'));
    }
  }, [accessToken, clientId, t]);

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, clientId, refresh]);

  useEffect(() => {
    if (!clientId) return undefined;
    const onVis = (): void => {
      pageVisibleRef.current = !document.hidden;
    };
    document.addEventListener('visibilitychange', onVis);
    const id = setInterval(() => {
      if (pageVisibleRef.current) void refresh();
    }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [accessToken, clientId, refresh]);

  // Mark received messages as read on render.
  useEffect(() => {
    if (!user) return;
    const userId = user.id;
    const unread = messages.filter(
      (m) => m.recipientUserId === userId && m.readAt === null,
    );
    for (const m of unread) {
      void api.markMessageRead(accessToken, m.id).catch(() => {
        /* silent */
      });
    }
  }, [accessToken, messages, user]);

  const send = async (): Promise<void> => {
    if (!clientId || !draft.trim()) return;
    setSending(true);
    setError(null);
    try {
      const sent = await api.sendMessage(accessToken, clientId, draft.trim());
      // Optimistically prepend (server returns reverse-chronological).
      setMessages((prev) => [sent, ...prev]);
      setDraft('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.errors.network'));
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s7">
        <p className="text-sm text-gray-2">{t('messages.notAuthenticated')}</p>
      </section>
    );
  }

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s7">
        <h1 className="font-display text-display-lg text-ink">{t('messages.title')}</h1>
        <p className="mt-s3 text-sm text-gray-2" data-testid="messages-no-thread">
          {viewerRole === 'client'
            ? t('messages.noClientProfile')
            : t('messages.pickAClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <h1 className="font-display text-display-lg text-ink">{t('messages.title')}</h1>
      <p className="mt-s2 text-sm text-gray-2">{t('messages.hint')}</p>

      <div className="mt-s5">
        {/* IMP-008 / NL-029 — composer cap matches the server's 4000-char
            limit; a live counter fades in past 3500 so long messages never
            silently hit the wall. */}
        <textarea
          className="w-full rounded-md border border-bone-3 bg-bone-1 p-s3 text-sm text-ink"
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('messages.composerPlaceholder')}
          data-testid="messages-composer"
          maxLength={MESSAGE_MAX}
        />
        <div className="mt-s2 flex items-center gap-s3">
          <button
            type="button"
            className="rounded-md bg-flame-ember px-s4 py-s2 font-medium text-bone-1 disabled:opacity-50"
            onClick={() => void send()}
            disabled={sending || !draft.trim()}
            data-testid="messages-send"
          >
            {sending ? t('messages.sending') : t('messages.send')}
          </button>
          {draft.length >= MESSAGE_COUNTER_FROM && (
            <span
              className={
                'ml-auto font-mono text-xs ' +
                (draft.length >= MESSAGE_MAX ? 'text-flame-ember' : 'text-gray-2')
              }
              data-testid="messages-counter"
            >
              {draft.length}/{MESSAGE_MAX}
            </span>
          )}
          {error && (
            <span className="text-sm text-flame-ember" data-testid="messages-error">
              {error}
            </span>
          )}
        </div>
      </div>

      <div className="mt-s6">
        {loading ? (
          <p className="text-sm text-gray-2">{t('common.labels.loading')}</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-gray-2" data-testid="messages-empty">
            {t('messages.empty')}
          </p>
        ) : (
          <ul className="space-y-s1" data-testid="messages-list">
            {/* WhatsApp-style bubbles: sent = right, received = left, sender
                chip atop each same-sender run, time + read-check inside the
                bubble. batch #4 #5 — the COACH's messages now pop in flame so
                they catch the client's eye; the client's own messages are calm
                parchment. Order stays newest-on-top (the server returns
                newest-first; the chip groups runs reading top→bottom). */}
            {messages.map((m, idx) => {
              const isMine = m.senderUserId === user.id;
              const prev = idx === 0 ? null : messages[idx - 1];
              const prevIsMine = prev ? prev.senderUserId === user.id : !isMine;
              const showSenderChip = idx === 0 || prevIsMine !== isMine;
              const undecryptable = m.body === '__h3m_undecryptable__';
              return (
                <li
                  key={m.id}
                  className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}
                  data-testid={`messages-row-${m.id}`}
                  data-mine={isMine ? 'true' : 'false'}
                >
                  {showSenderChip && (
                    <span
                      className={`mt-s2 mb-s1 px-s1 text-[10px] font-semibold uppercase tracking-wide text-gray-1 ${
                        isMine ? 'text-right' : 'text-left'
                      }`}
                    >
                      {isMine ? t('messages.you') : t('messages.them')}
                    </span>
                  )}
                  <div
                    className={`max-w-[78%] rounded-2xl px-s3 py-s2 shadow-sm ${
                      isMine
                        ? 'bg-bone-2 border border-bone-3 text-ink rounded-br-sm'
                        : 'bg-flame text-bone rounded-bl-sm'
                    }`}
                  >
                    {undecryptable ? (
                      <p
                        className={`whitespace-pre-wrap text-sm italic ${
                          isMine ? 'text-gray-1' : 'text-bone/80'
                        }`}
                        data-testid={`messages-undecryptable-${m.id}`}
                      >
                        {t('messages.undecryptable')}
                      </p>
                    ) : (
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.body}</p>
                    )}
                    <div
                      className={`mt-s1 flex items-center justify-end gap-s1 text-[10px] ${
                        isMine ? 'text-gray-2' : 'text-bone/75'
                      }`}
                    >
                      <time dateTime={m.createdAt}>{shortTime(m.createdAt)}</time>
                      {isMine && m.readAt && (
                        <span aria-label={t('messages.read')} title={t('messages.read')}>
                          ✓
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
